import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { animated, useSpring } from 'react-spring';
import useInterval from '@use-it/interval';
import { useSelector, useDispatch } from 'react-redux';

import LeaderboardTable from './BoardTable';
import Spinner from '../../../shared/_TBComponents/Spinner';
import SubText from '../../../shared/_TBComponents/SubText';
import { loadMoreLeaderboard, applyPendingReload } from '../ducks/leaderboardActions';
import { TABLE_MARGIN_TOP } from '../boardConstants';

const LoadMoreContainer = styled('div')`
  display: flex;
  width: 100%;
  height: 60px;
  padding: 20px 15px;
  justify-content: center;
  position: relative;
`;

// Fixed clearance below the table for the Load More spinner / "End of
// Leaderboard" text — unlike TABLE_MARGIN_TOP, this doesn't grow with
// scaleFactor; TV Mode doesn't scale that indicator area.
const TABLE_BOTTOM_PADDING = 30;

const BoardTableStyles = {
  overflow: 'scroll',
  flexGrow: 1,
  paddingBottom: `${TABLE_BOTTOM_PADDING}px`,
};

const LeaderboardTableWrapper = ({
  boardWidth,
  id,
  scaleFactor,
}) => {
  const leaderboardData = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardData,
  );
  const leaderboardForm = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].leaderboardForm,
  );
  const leaderboardSettings = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].settings,
  );
  const isEndOfList = useSelector((state) => state.leaderboard.data.leaderboards[id].isEndOfList);
  const isLeaderboardLoading = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].isLeaderboardLoading,
  );
  const pendingReloadData = useSelector(
    (state) => state.leaderboard.data.leaderboards[id].pendingReloadData,
  );
  const dispatch = useDispatch();

  const tableRef = useRef();
  const tableContainerRef = useRef();
  const [tableHeight, setTableHeight] = useState();
  const [tableContainerHeight, setTableContainerHeight] = useState(0);
  const [tableScroll, setTableScroll] = useState(0);
  const [currentScroll, setCurrentScroll] = useState(0);
  const [data, setData] = useState([]);

  const loadTransition = useSpring({
    position: 'absolute',
    opacity: isLeaderboardLoading ? 1 : 0,
  });

  const endTransition = useSpring({
    position: 'absolute',
    opacity: isEndOfList ? 1 : 0,
  });

  const autoScrollTransition = useSpring({
    scroll: tableScroll,
    from: { scroll: 0 },
  });

  const {
    autoScroll,
  } = leaderboardSettings;

  // Two separately sourced terms — only the top margin scales. See TABLE_MARGIN_TOP.
  const tableTopMargin = Math.round(TABLE_MARGIN_TOP * scaleFactor);

  const initialScrollLocation = tableRef.current ? tableRef.current.offsetTop : 0;

  const tableEnd = (initialScrollLocation - tableHeight)
    + tableContainerHeight - (tableTopMargin + TABLE_BOTTOM_PADDING) + 10;

  const loadMore = () => {
    if (!isEndOfList) {
      dispatch(loadMoreLeaderboard(leaderboardForm, id));
    }
  };

  // The top 3 are on the podium (BoardPodiums.js); keep these counts in sync.
  useEffect(() => {
    setData(leaderboardData.slice(3));
  }, [leaderboardData]);

  // Safe to apply immediately: a reload covers exactly the loaded pages, so it
  // never resets pagination or scroll.
  useEffect(() => {
    if (pendingReloadData) {
      dispatch(applyPendingReload(id));
    }
  }, [pendingReloadData]);

  useInterval(() => {
    setTableScroll((prev) => prev + (tableContainerHeight / 2));
  }, !autoScroll ? null : 5000);

  // Tracked live, not measured once: auto-scroll steps and the load-more
  // threshold both depend on the current height.
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setTableContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (autoScroll === true && isEndOfList && (currentScroll <= tableEnd)) {
      setTimeout(() => {
        setTableScroll(0);
        setCurrentScroll(0);
      }, 1500);
    }
  }, [tableScroll]);

  return (
    <animated.div
      ref={tableContainerRef}
      scrollTop={autoScrollTransition.scroll}
      style={BoardTableStyles}
    >
      <LeaderboardTable
        boardWidth={boardWidth}
        handleLoadMore={loadMore}
        leaderboardData={data}
        leaderboardForm={leaderboardForm}
        scaleFactor={scaleFactor}
        tableEnd={tableEnd}
        tableRef={tableRef}
        setTableHeight={setTableHeight}
        setCurrentScroll={setCurrentScroll}
      />
      <LoadMoreContainer>
        <animated.div style={loadTransition}>
          <Spinner
            saving
            darkTheme
            large={false}
          />
        </animated.div>
        <animated.div style={endTransition}>
          <SubText>
            End of Leaderboard
          </SubText>
        </animated.div>
      </LoadMoreContainer>
    </animated.div>
  );
};

LeaderboardTableWrapper.propTypes = {
  boardWidth: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  scaleFactor: PropTypes.number.isRequired,
};

export default LeaderboardTableWrapper;
